/**
 * @fileoverview bill extraction API for solarIQ
 * 
 * handles bill parsing from various submitted file formats (PDF, Excel, Images)
 * uses AI for intelligent extraction with multiple fallback strategies
 */

import { extractTextFromExcel, extractTextFromPdf, extractJpegsFromPdf } from "../utils/billParsing.js";
import {AI_ENDPOINT, apiHeaders} from "./apiConfig.js"


/**
 * parseJson:
 * consolidated helper function
 * strips markdown fences and parses JSON from AI response
 * 
 * @param {Object} data - raw API response
 * @returns {Object|null} 
 * @private
 */
function parseJson(data) {
    try {
        const raw = (data?.content?.[0]?.text || "{}").replace(/```[a-z]*/g, "").replace(/```/g, "").trim();
        return JSON.parse(raw);

    } catch {
        return null;
    }
}

/**
 * extractBillAI:
 * extracts bill data from uploaded file using AI
 * handles the aforementioned PDF, Excel and image formats
 * 
 * process:
 * 1) PDF: try text extraction -> try JPEG extraction -> beta PDF API (PDF.js)
 * 2) Excel: extract to csv format, send to AI
 * 3) Images: send directly to vision-capable AI
 * 
 * @param {string} b64 - base64 encoded file data
 * @param {string} mime - MIME type of current file
 * @param {Object} file - file object (needed for name/extension checks)
 * 
 * @returns {Promise<Object|null>} extracted bill data OR null
 *  bill data: {monthly_bill, kwh_usage, rate_per_kwh, tariff_type, bill_type } 
 */
export async function extractBillAI(b64, mime, file = null) {
    const isPDF = mime === "application/pdf";
    const isExcel = mime === "application/vnd.ms-excel"
    || mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    || (file && (file.name.endsWith(".xls") || file.name.endsWith(".xlsx")));

    const extractPrompt = `You are reading a South African municipal electricity bill. Return ONLY a JSON object, no other text.
CRITICAL — find the exact following values:

1. monthly_bill = The ELECTRICITY-ONLY charge for this billing period (NOT the full municipal account total):

- City of Cape Town bills include rates, water, refuse, sewerage AND electricity on one statement.
- Eskom Direct bills (NOT City of Cape Town) have multiple charge components - 'Service Charge', 'Network Capacity Charge', 'Network Demand Charge', 'Energy Charge', etc.
- For Eskom Direct bills, always use the 'TOTAL AMOUNT DUE' line as monthly_bill. This is the value INCLUDING VAT.
- As a fallback if no value with VAT is present, use the exVAT value 'TOTAL CHARGES FOR BILLING PERIOD'.
- NO individual items even if the line is the largest.
- Find the ELECTRICITY section specifically. Look for "ELECTRICITY" heading with its subtotal (e.g. 13816.38).
- Do NOT use "Total Liability", "Total Due", or amounts that include arrears or other services.
- If you can only find a single electricity-only total, use that. If the bill is electricity-only (no other services), use the total amount due.
- For body corporate/commercial bills that are electricity-only, use the full total due.

2. kwh_usage = kWh consumed in the billing period. (IMPORTANT: normalise to 30 days):
- CRITICAL METER FORMAT: City of Cape Town meters show "176.252(X100)kW" or "176.252(x100)kW" in the Units Used column. 
This means 176.252 × 100 = 17625.2 kWh. ALWAYS multiply by 100 when you see (X100) or (x100).
- The most reliable kWh source is the line: "Consumption XXXXX.XXX kWh" or "Meter no: XXXXXXX / Consumption 17625.200 kWh / Daily average 587.507 kWh"
- The billing period shows e.g. "30 Days" — if exactly 30 days, no normalisation needed.
- If billing period is NOT 30 days: kwh_usage = raw_kwh ÷ actual_days × 30
- Example: 3553 kWh over 34 days → 3553 ÷ 34 × 30 = 3135
- DO NOT use small numbers like 176.252 as the kWh — that is a meter index in hundreds. The actual kWh for large commercial accounts is typically 5,000–50,000 kWh/month.

3. rate_per_kwh:
- The Rand/kWh tariff rate shown on the bill, e.g. 2.7986 or 1.9948.

4. tariff_type - classify as one of three values:
- "commercial" if you see "Small Power User", "Large Power User", "Body Corporate", "Commercial", "C&I", "Business"
- "agriculture" if you see "Landrate", "Agricultural", "Farm", "Agri", "Rurale", "Landbou"
- "residential" for all other standard domestic/municipal tariffs
- pay attention to what is under "TARIFF NAME".

5. account_number:
- account or customer number

6. address:
- physical service/installation address only. In human-readable format.
- ignore PO Box, POSBUS or postal addresses - these are not installation addresses. Do not include the box number.
Example: "POSBUS 20 TOWN 1111" -> "TOWN 1111" (drop POSBUS and the number, keep town and postal code).
- convert "STUDY STR NO 13 ERF 9331 FLAMINGOVLEI 7441" to "13 Study Street, Flamingovlei 7441". Use Title Case. Strip ERF numbers.
- if the only address information is a PO Box in a town, or just the town/city/region name itself, return just the name and postal code if present e.g. "Town 1111".
- if no location information or physical address found at all, return "". 

7. bill_type - Classify this document:
- "electricity" = has an electricity consumption section with kWh and/or electricity charges
- "rates_only" = only property rates, no electricity section at all
- "water_only" = only water charges, no electricity
- "rates_and_water" = rates + water but NO electricity section
- "not_a_bill" = not a utility bill at all

- IMPORTANT: A City of Cape Town bill that ONLY shows "PROPERTY RATES" with no ELECTRICITY section is "rates_only" — do NOT try to extract electricity data from it.

REMINDER:
- pull ALL values from analysed file.
- return values extracted FROM THAT ANALYSED TEXT ONLY.
- use 0 if truly not found, no null values in output.

FORMAT EXAMPLE BELOW, DO NOT USE THESE NUMBERS OR ANY VALUES IN THE EXAMPLE:

Return this structure:
{"monthly_bill":42846.18,"kwh_usage":17625,"rate_per_kwh":1.9948,"tariff_type":"commercial"/"agriculture"/"residential","account_number":"211999834",
"address":"5 Henry Vos Close Asla Park","bill_type":"electricity"}

For a "rates-only" bill return:
{"monthly_bill":0,"kwh_usage":0,"rate_per_kwh":0,"tariff_type":"residential","account_number":"224945522",
"address":"10 Prestige Place, 5 Henry Vos Close, Asla Park","bill_type":"rates_only"}

NOTE: The electricity example above is from a real body corporate bill — 17625 kWh is correct for a large complex (not 176 kWh). 
AlWAYS CHECK: does the kWh make sense for the bill amount? R42,846 ÷ R1.9948 = ~21,478 kWh before VAT/service charges — so 17,625 kWh is plausible.`;

    try {
        let imageB64 = null;
        let imageMime = null;

        // ------ PDF PROCESSING ------
        if (isPDF) {

            // Always extract text from ALL pages using PDF.js, then send text to AI
            // (Image extraction only captures page 1 — kWh data is on page 2 of CoCT bills)

            // STEP 1: PDF.js extraction
            const pdfText = await extractTextFromPdf(b64);
            console.log("PDF text extracted:", pdfText ? pdfText.slice(0, 400) : "EMPTY");

            // STEP 2: check for readable text removed from PDF binary     
            // filtering out pdf structure markers to detect real content
            console.log("Checking for readable text:");
            const cleanText = pdfText ? pdfText.replace(/%PDF|\/Type|\/Catalog|\/Pages|\/Page|endobj|stream|endstream|\(X100\)/g,"").trim() : "";
            const hasReadableText = cleanText.length > 200 && /electricity|consumption|kwh|eskom|account|tariff/i.test(pdfText);

            if (hasReadableText) {
                console.log("PDF has readable text. Send to AI.");
                const pdfRes = await fetch(AI_ENDPOINT, {
                        method: "POST", 
                        headers: apiHeaders(),
                        body: JSON.stringify({ 
                            task: "vision",
                            max_tokens: 150,  //400
                            messages: [{
                                role: "user", 
                                content: `Bill text extracted from PDF:\n\n${pdfText.slice(0,10000)}\n\n${extractPrompt}`
                        }]
                    })
                });

                const rawText = await pdfRes.text();
                console.log("PDF background raw response:", rawText?.slice(0, 200));

                if (!rawText) {
                    console.warn("Empty response for background function - local dev may not support.");
                    return null;
                }

                let pdfData;

                try {
                    pdfData = JSON.parse(rawText);
                } catch (e) {
                    console.warn("Background function response was not valid JSON:", rawText?.slice(0,100));
                    return null;
                }

                if (pdfRes.ok) {

                    const parsed = parseJson(pdfData);
                    console.log("PDF text AI parsed: ", parsed);
                    if (parsed && parsed.bill_type !== "not_a_bill" && parsed.monthly_bill > 0) {
                        return parsed;
                    }
                        
                    console.warn("Invalid bill result. Attempting image extraction.")
                }
            }

            // STEP 3: text extraction failed, so try extracting embedded JPEGs 
            console.log("PDF text extraction was unsuccessful. Trying JPEG extraction from scanned PDF.")
            const jpegs = extractJpegsFromPdf(b64);

            if (jpegs.length > 0) {
                console.log(`FOUND ${jpegs.length} JPEG(s) in PDF. Largest image sent to AI API.`);
                imageB64 = jpegs[0];
                imageMime = "image/jpeg";
            }

            else {
                // STEP 4: no text layer and no embedded JPEGs — unable to extract automatically
                console.log("PDF.js got no text and no embedded JPEGs found - unable to extract details. Your manual amount will be used instead");
                return null;
                
                /* const rawB64 = b64.includes(",") ? b64.split(",")[1] : b64;
                
                const pdfRes = await fetch(AI_ENDPOINT, {
                    method: "POST", 
                    headers: apiHeaders(),
                    body: JSON.stringify({
                        //model: "claude-haiku-4-5-20251001", 
                        max_tokens: 400,
                        beta: "pdfs-2024-09-25",
                        messages: [{ 
                            role: "user", 
                            content: [
                                { type: "document", source: { type: "base64", media_type: "application/pdf", data: rawB64 } },
                                { type: "text", text: extractPrompt }
                            ]
                        }]
                    })
                });

                const pdfData = await pdfRes.json();
                try {
                    const parsed = parseJson(pdfData);
                    if (!parsed || parsed._error === "FEATURE_NOT_SUPPORTED") {
                        console.log("Scanned bill unable to be read automatically - your manual amount will be used instead.");
                        return null;
                    }

                    return parsed; 
                

                } catch(e) { 
                    console.log("Catch: returning null"); 
                    return null; 
                }

                */
            }


        // ------ EXCEL PROCESSING ------
        } else if (isExcel) {
            const excelText = await extractTextFromExcel(b64);
            if (!excelText) return null;

            const res = await fetch(AI_ENDPOINT, {
                method: "POST", 
                headers: apiHeaders(),
                body: JSON.stringify({
                    task: "vision",
                    max_tokens: 150,
                    messages: [{ 
                        role: "user",
                        content: `Electricity bill data:\n${excelText.slice(0, 8000)}\n\n${extractPrompt}`
                    }]
                })
            });

            const rawText = await res.text();
            if (!rawText) return null;
            let excelData;
            try {
                excelData = JSON.parse(rawText);
            } catch(e) {
                console.warn("Excel background response was not valid JSON:", rawText?.slice(0,100));
                return null;
            }

            return parseJson(excelData);
        } 
        
        // ------ IMAGE PROCESSING ------
        else {
            imageB64 = b64.includes(",") ? b64.split(",")[1] : b64;
            imageMime = mime;
        }

        // SEND IMAGE TO AI API (images + extracted JPEG from PDFs)
        if (imageB64) {
            console.log(`Sending ${imageMime} to AI API`);

            const res = await fetch(AI_ENDPOINT, {
                method: "POST", 
                headers: apiHeaders(),
                body: JSON.stringify({
                    task: "vision",
                    max_tokens: 150,
                    messages: [{ 
                        role: "user",
                        content: [
                            { type: "image", source: { type: "base64", media_type: imageMime, data: imageB64 } },
                            { type: "text", text: extractPrompt }
                        ]
                    }]
             
                })
            });


            const rawText = await res.text();
            if (!rawText) return null;

            let imageData;
            try {
                imageData = JSON.parse(rawText);
            } catch (e) {
                console.warn("Image background response was not valid JSON:", rawText?.slice(0,100));
                return null;
            }

            if (!res.ok) {
                console.error("AI error: ", res.status, imageData?.error?.message);
                return null;
            }

            const parsed = parseJson(imageData);
            console.log("Image content AI parsed:", parsed);

            if (!parsed || parsed._error === "NO_API_KEY") {
                console.log("Bill image could not be read automatically - your manual amount will be used instead.");
                return null;
            }
            /*const imageData = await res.json();

            if (!res.ok) {
            console.error("AI error:", res.status, imageData?.error?.message);
            return null;
            }

            try {
            const parsed = parseJson(imageData);
            console.log("image content AI parsed: ", parsed)

            if (!parsed || parsed._error === "NO_API_KEY") {
                console.log("Bill image couldn't be read automatically — your manual amount will be used instead.");
                return null;
            }*/

            return parsed;
        }
    
        return null;

    } catch (e) {
        console.error("extractBillAI error:", e);
        return null;
    }
}
    
